import { useQuery } from '@tanstack/react-query';

import { clientApi } from '@tankef/api-client';

import {
  mapBalanceAfterCents,
  mapFeeBreakdown,
  mapMovementCounterparty,
  mapMovementCurrency,
  mapMovementDate,
  mapMovementNumericReference,
  mapMovementOperationType,
  mapMovementTrackingKey,
  type Movement,
  type MovementsFilter,
} from '@/domains/wallet/wallet/api';

const STATUS_MAP = {
  liquidado: 'completed',
  completado: 'completed',
  completed: 'completed',
  pendiente: 'pending',
  pending: 'pending',
  fallido: 'failed',
  failed: 'failed',
  cancelado: 'cancelled',
  cancelled: 'cancelled',
} as const;

export function useAccounts() {
  return useQuery({
    queryKey: ['accounts'],
    queryFn: () => clientApi.accounts(),
    staleTime: 30_000,
  });
}

export function usePrimaryAccount() {
  return useQuery({
    queryKey: ['accounts', 'primary'],
    queryFn: () => clientApi.primaryAccount(),
    staleTime: 30_000,
  });
}

export function useAccountTransactions(
  accountId: string | undefined,
  filters?: MovementsFilter,
  page = 1,
  pageSize = 10
) {
  return useQuery({
    queryKey: ['accounts', accountId, 'transactions', filters, page, pageSize],
    queryFn: async () => {
      if (!accountId) {
        throw new Error('accountId is required');
      }

      const response = await clientApi.accountTransactions(accountId, {
        page,
        per_page: pageSize,
        date_from: filters?.startDate,
        date_to: filters?.endDate,
        direction: filters?.direction,
        entry_type: filters?.entryType,
      });

      const movements: Movement[] = response.data.map(m => {
        const type = m.movement_type === 'abono' ? 'income' : 'expense';
        const status = STATUS_MAP[m.status as keyof typeof STATUS_MAP] ?? 'pending';

        return {
          id: m.id,
          type,
          status,
          description: m.description,
          reference: m.reference ?? undefined,
          amountCents: m.amount_cents,
          currency: mapMovementCurrency(m),
          date: mapMovementDate(m),
          accountId: m.account_id ?? undefined,
          journalEntryId: m.journal_entry_id,
          entryNumber: m.entry_number,
          entryDate: m.entry_date,
          movementType: m.movement_type,
          direction: m.direction,
          amountFormatted: m.amount,
          folio: m.folio,
          rawStatus: m.status,
          statusLabel: m.status_label,
          entryType: m.entry_type,
          feeBreakdown: mapFeeBreakdown(m.fee_breakdown),
          balanceAfterCents: mapBalanceAfterCents(m.balance_after_cents),
          counterparty: mapMovementCounterparty(m.counterparty),
          trackingKey: mapMovementTrackingKey(m.spei_tracking_key),
          operationType: mapMovementOperationType(m.operation_type),
          numericReference: mapMovementNumericReference(m.numeric_reference),
        };
      });

      return {
        movements,
        meta: {
          page: response.meta.current_page,
          pageSize: response.meta.per_page,
          total: response.meta.total_count,
          totalPages: response.meta.total_pages,
        },
      };
    },
    enabled: !!accountId,
    staleTime: 30_000,
  });
}
